import React from 'react';
import { Query } from 'react-apollo';
import { styled } from 'linaria/react';
import BlogHeader from './BlogHeader';
import Head from '@jetshop/core/components/Head';
import blogArticleQuery from './BlogArticleQuery.gql';
import styleLookArticleQuery from './styleLookArticleQuery.gql';
import { ContentPadding } from '../Layout/Content/Content';
import MaxWidth, { PagePadding } from '../Layout/MaxWidth';
import BlogArticleLoadingState from './BlogArticleLoadingState';
import { theme } from '../Theming/Theming';
import BlogItemList from './BlogItemList';
import t from '@jetshop/intl';
import { ProductListHeader } from '../ProductPage/StyledComponents';
import { useLocation } from 'react-router';
import { useBlog } from './hook/useBlog';
import TextContent from '../ui/TextContent';
import { RowProducts } from '../ui/ProductRow/RowProducts';
import StylesProducts from './StylesProducts';
import {
  ContentRender,
  convertPropsToObject
} from '../ContentRender/ContentRender';
import Image from '@jetshop/ui/Image';
import { BreadcrumbsWrapper } from '../Layout/BreadcrumbsWrapper';
import { ScrollBox } from '../ui/ScrollBox';
import { Above } from '@jetshop/ui/Breakpoints';
import { Route } from 'react-router-dom';
import NotFoundPage from '../NotFoundPage';

export const BlogArticleMaxWidth = styled('div')`
    max-width: 800px;
    margin: 0 auto;
`;

const BlogArticleWrapper = styled('div')`
    margin-top: 2.5rem;

    ${ContentPadding}
    .blog-article-main-area {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    .blog-article-content {
        h2,
        h3,
        h4,
        h5 {
            margin: 1.5em 0 1em;
        }

        p {
            line-height: 1.5em;
            margin-bottom: 1rem;
        }

        img {
            margin: 1.5rem 0;
        }
    }

    .blog-article-products {
        ${theme.below.lg} {
            margin-top: 2rem;
        }
    }
`;

const StyleArticleWrapper = styled('div')`
    padding: 1rem 0 3rem;

    ${theme.above.lg} {
        padding-top: 2rem;
    }

    &[data-noheader='true'] {
        .style-article-info {
            width: 100%;

            ${theme.above.lg} {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;

                > div {
                    width: 48%;
                }
            }
        }
    }

    .style-article-images:not(.slider-box-wrapper) {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        height: 100%;
        position: sticky;
        top: ${theme.desktopHeader.height.lg + 10 + 'px'};

        > .image-wrapper {
            width: 100%;
            margin-bottom: 2%;

            ${theme.above.xl} {
                width: 49%;
            }
        }
    }

    .slider-box-wrapper {
        .slider-items {
            > div {
                width: 75%;
                margin-right: 2%;
            }
        }
    }

    .style-article-info {
        .category-name {
            margin: 0 0 1rem 0;
        }
    }

    ${theme.above.lg} {
        display: flex;
        justify-content: space-between;

        .style-article-images {
            width: 49%;
        }

        .style-article-info {
            width: 49%;
        }
    }
`;

const StyledProductListHeader = styled(ProductListHeader)`
    margin-top: 4rem;
`;

const BlogArticle = ({ result, configObject }) => {
  const { data } = result;
  const category = (data && data.route && data.route.object) || null;

  const { styleLook } = configObject;

  const { articles, menuCategories } = useBlog({
    ...configObject,
    parentId: category && category.parentId,
    exclude: [category && category.id]
  });

  // NOT VALID ROUTE, RETURN 404
  if (!result?.loading && !category) {
    return (
      <>
        <Route
          render={({ staticContext }) => {
            if (staticContext) {
              staticContext.statusCode = 404;
            }
            return <NotFoundPage />;
          }}
        />
      </>
    );
  }

  if (result.loading) {
    return <BlogArticleLoadingState />;
  }

  const { route } = data;
  const breadcrumbProps = {
    breadcrumbs: route?.breadcrumbs,
    parents: route?.parents
  };

  const parentCategory =
    menuCategories &&
    menuCategories.filter((cat) => {
      return cat.id === category.parentId;
    });

  const specialCategoryHeader = category?.data?.items?.filter(
    (item) => item.type === 'special-category-images'
  )[0]?.children;

  return (
    <>
      {category.head && <Head data={category} route={route} />}
      {styleLook ? (
        <>
          <MaxWidth>
            <StyleArticleWrapper
              data-noheader={!specialCategoryHeader}
              data-hasproducts={
                category.products && category.products.result.length > 0
                  ? true
                  : false
              }
            >
              {specialCategoryHeader && (
                <Above breakpoint="lg">
                  {(matches) => {
                    const Tag = !matches ? ScrollBox : 'div';
                    return (
                      <Tag className="style-article-images">
                        {specialCategoryHeader?.map((imageItem, index) => {
                          const props = convertPropsToObject(imageItem);
                          return (
                            <div className="image-wrapper" key={index}>
                              <Image
                                src={props.imageUrl}
                                alt={category.name}
                                sizes={[1, 1, 1 / 4, 1 / 4]}
                                aspect={'3:4'}
                                crop={true}
                                cover
                              />
                            </div>
                          );
                        })}
                      </Tag>
                    );
                  }}
                </Above>
              )}
              <div className="style-article-info">
                <div className="info">
                  <BreadcrumbsWrapper {...breadcrumbProps} />
                  <h2 className="category-name">{category.name}</h2>
                  {category.content && (
                    <TextContent content={category.content} />
                  )}
                </div>
                {category.products && category.products.result && (
                  <StylesProducts category={category} />
                )}
              </div>
            </StyleArticleWrapper>
          </MaxWidth>
          <ContentRender data={category.data} />
        </>
      ) : (
        <>
          <BlogHeader
            category={category}
            parentCategory={parentCategory && parentCategory[0]}
            breadcrumbProps={breadcrumbProps}
          />
          <PagePadding style={{ marginBottom: '5rem' }}>
            <BlogArticleMaxWidth>
              <BlogArticleWrapper
                data-hasproducts={
                  category.products && category.products.result.length > 0
                    ? true
                    : false
                }
              >
                <div className="article-inner">
                  {category.content && (
                    <TextContent content={category.content} />
                  )}
                </div>
              </BlogArticleWrapper>
            </BlogArticleMaxWidth>
            {category.products && category.products.result && (
              <div className="blog-article-products">
                <RowProducts category={category} slider={true} />
              </div>
            )}
            <StyledProductListHeader>
              {t('You will also like this')}
            </StyledProductListHeader>
            <BlogItemList categories={articles} blogPage={false} />
          </PagePadding>
        </>
      )}
    </>
  );
};

const ArticleComponent = ({ configObject }) => {
  const location = useLocation();
  const query = configObject?.styleLook
    ? styleLookArticleQuery
    : blogArticleQuery;
  return (
    <Query query={query} variables={{ path: location.pathname }}>
      {(result) => <BlogArticle configObject={configObject} result={result} />}
    </Query>
  );
};

export default ArticleComponent;
